import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { Leaf, Building2, Send, Lock } from "lucide-react";
import { logger } from "../../lib/logger";
import CollaborationsSection from "./CollaborationsSection";
import DomainMatchInvites from "./DomainMatchInvites";
import PrintersSection from "./PrintersSection";

export default function OrganisationTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManageOrg = isAdmin || !!user?.is_org_owner;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [sub, setSub] = useState("hybrid");
  const [accountingEmails, setAccountingEmails] = useState("");
  const [saving, setSaving] = useState(false);

  // External transfer form
  const [myListings, setMyListings] = useState([]);
  const [tfListing, setTfListing] = useState("");
  const [tfType, setTfType] = useState("forsaljning");
  const [tfReceiver, setTfReceiver] = useState("");
  const [tfDate, setTfDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: ml }] = await Promise.all([
        api.get("/org/me/summary"),
        api.get("/listings/mine"),
      ]);
      setSummary(s);
      setMyListings(ml || []);
      if (s?.organization) {
        setName(s.organization.name || "");
        setSub(s.organization.subscription || "hybrid");
        setAccountingEmails((s.organization.accounting_emails || []).join("\n"));
      }
    } catch (err) {
      logger.warn("OrganisationTab: failed to load summary/listings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const emails = accountingEmails
        .split(/[\n,;]/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@"));
      await api.patch("/org/me", { name, subscription: sub, accounting_emails: emails });
      toast.success(t("org.saved"));
      await load();
    } catch {
      toast.error(t("dash.failed"));
    } finally {
      setSaving(false);
    }
  };

  const createTransfer = async (e) => {
    e.preventDefault();
    if (!tfListing || !tfReceiver.trim()) {
      toast.error(t("add.fillErr"));
      return;
    }
    try {
      await api.post(`/listings/${tfListing}/external-transfer`, {
        extern_typ: tfType,
        extern_mottagare: tfReceiver,
        datum: tfDate || null,
      });
      toast.success(t("org.transfer.created"));
      setTfListing(""); setTfReceiver(""); setTfDate("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("org.transfer.failed"));
    }
  };

  if (loading) {
    return <div className="label-tech">{t("detail.loading")}</div>;
  }

  const isIntern = sub === "intern";
  const canExternal = sub === "hybrid";

  return (
    <div className="space-y-8" data-testid="org-tab">
      <div className="label-tech">{t("org.label")}</div>
      <h2 className="font-display text-3xl tracking-tight">{t("org.title")}</h2>

      {/* Org settings — editable by platform admin OR org owner */}
      {canManageOrg ? (
        <form onSubmit={saveOrg} className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="org-settings-form">
          <div className="md:col-span-2 flex items-center gap-2 label-tech"><Building2 size={14} /> {t("org.name")}</div>
          <div className="md:col-span-2">
            <input className="input-tech" value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name-input" />
          </div>
          <div>
            <div className="label-tech mb-2">{t("org.subscription")}</div>
            <select className="input-tech" value={sub} onChange={(e) => setSub(e.target.value)} data-testid="org-subscription-select">
              <option value="intern">{t("org.sub.intern")}</option>
              <option value="hybrid">{t("org.sub.hybrid")}</option>
              <option value="oppen">{t("org.sub.oppen")}</option>
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="org-save-btn">
              {saving ? "…" : t("org.save")}
            </button>
          </div>
          <div className="md:col-span-2 border-t border-[var(--ink-200)] pt-4 mt-2">
            <div className="label-tech mb-2">{t("rental.accountingEmails")}</div>
            <textarea
              rows={3}
              className="input-tech font-mono-tech text-xs"
              placeholder="faktura@foretag.se&#10;ekonomi@foretag.se"
              value={accountingEmails}
              onChange={(e) => setAccountingEmails(e.target.value)}
              data-testid="org-accounting-emails-input"
            />
            <div className="text-xs text-[var(--ink-500)] mt-1">{t("rental.accountingEmailsHelp")}</div>
          </div>
        </form>
      ) : (
        <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="org-settings-readonly">
          <div className="md:col-span-2 flex items-center gap-2 label-tech"><Building2 size={14} /> {t("org.name")}</div>
          <div className="md:col-span-2 font-display text-lg">{name || "—"}</div>
          <div>
            <div className="label-tech mb-2">{t("org.subscription")}</div>
            <div className="font-display text-lg">{t(`org.subShort.${sub}`) || sub}</div>
          </div>
          <div className="md:col-span-2 label-tech inline-flex items-center gap-1 text-[var(--ink-700)]">
            <Lock size={12} /> {t("org.adminOnlyHint")}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
        <Stat label={t("org.listingsTotal")} value={summary?.listings_total ?? 0} testId="org-stat-total" />
        <Stat label={t("org.internal")} value={summary?.internal_count ?? 0} testId="org-stat-internal" />
        <Stat label={t("org.external")} value={summary?.external_count ?? 0} testId="org-stat-external" />
        <Stat label={t("org.published")} value={summary?.published_count ?? 0} testId="org-stat-published" />
        <Stat label={t("org.co2eTotal")} value={`${summary?.co2e_total_kg ?? 0} ${t("org.kg")}`} highlight testId="org-stat-co2e" />
      </div>
      <div className="label-tech">{t("org.kalla")}</div>

      {/* Per category */}
      {summary?.co2e_per_category && Object.keys(summary.co2e_per_category).length > 0 && (
        <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="org-per-category">
          <div className="label-tech mb-3">{t("org.co2ePerCat")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(summary.co2e_per_category).map(([cat, val]) => (
              <div key={cat} className="flex items-center justify-between border border-[var(--ink-200)] px-3 py-2">
                <span className="text-sm">{cat}</span>
                <span className="label-tech"><Leaf size={12} className="inline" /> {val} {t("org.kg")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* External transfer (hybrid only) */}
      {canExternal && (
        <form onSubmit={createTransfer} className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="external-transfer-form">
          <div className="md:col-span-2 label-tech flex items-center gap-2"><Send size={14} /> {t("org.newTransfer")}</div>
          <div>
            <div className="label-tech mb-2">{t("org.transfer.selectListing")}</div>
            <select className="input-tech" value={tfListing} onChange={(e) => setTfListing(e.target.value)} data-testid="transfer-listing-select" required>
              <option value="">—</option>
              {myListings.map((l) => (
                <option key={l.id} value={l.id}>{l.title}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="label-tech mb-2">{t("org.transfer.type")}</div>
            <select className="input-tech" value={tfType} onChange={(e) => setTfType(e.target.value)} data-testid="transfer-type-select">
              <option value="forsaljning">{t("org.transfer.forsaljning")}</option>
              <option value="overlatelse">{t("org.transfer.overlatelse")}</option>
              <option value="donation">{t("org.transfer.donation")}</option>
            </select>
          </div>
          <div>
            <div className="label-tech mb-2">{t("org.transfer.receiver")}</div>
            <input className="input-tech" value={tfReceiver} onChange={(e) => setTfReceiver(e.target.value)} data-testid="transfer-receiver-input" required />
          </div>
          <div>
            <div className="label-tech mb-2">{t("org.transfer.date")}</div>
            <input type="date" className="input-tech" value={tfDate} onChange={(e) => setTfDate(e.target.value)} data-testid="transfer-date-input" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary" data-testid="transfer-create-btn">{t("org.transfer.create")}</button>
          </div>
        </form>
      )}
      {isIntern && (
        <div className="border border-[var(--ink-200)] bg-[var(--surface-alt)] p-4 label-tech" data-testid="intern-transfer-note">
          {t("org.transfer.forbiddenIntern")}
        </div>
      )}

      {/* Collaborations (admin only) */}
      {canManageOrg && <DomainMatchInvites />}
      {canManageOrg && <CollaborationsSection />}

      {/* Network printers — visible to all org members; only managers can edit */}
      <PrintersSection canManage={canManageOrg} />

      {/* Transfers list */}
      <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="org-transfers-list">
        <div className="label-tech mb-3">{t("org.transfers")}</div>
        {(!summary?.transfers || summary.transfers.length === 0) ? (
          <div className="label-tech">{t("org.noTransfers")}</div>
        ) : (
          <div className="divide-y divide-[var(--ink-200)]">
            {summary.transfers.map((tr) => (
              <div key={tr.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="font-display text-base">{tr.listing_title}</div>
                  <div className="label-tech">{t(`org.transfer.${tr.extern_typ}`)} · {tr.extern_mottagare} · {tr.datum}</div>
                </div>
                <span className="tag tag-moss">{tr.co2e_kg} {t("org.kg")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, testId }) {
  return (
    <div className={`p-4 ${highlight ? "bg-[var(--moss)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid={testId}>
      <div className="label-tech opacity-80">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
